import { useState } from 'react'
import { Search, RefreshCw, RotateCcw } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useModelGroups } from '@/hooks/useStoreModels'

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '+store_number', label: 'Store # Asc' },
  { value: '-store_number', label: 'Store # Desc' },
  { value: '-allocation_percent', label: 'Alloc % High-Low' },
  { value: '+allocation_percent', label: 'Alloc % Low-High' },
  { value: '-total_sales', label: 'Sales High-Low' },
  { value: '+total_sales', label: 'Sales Low-High' },
  { value: '-total_units', label: 'Units High-Low' },
  { value: '+total_units', label: 'Units Low-High' },
]

interface MembersFilterBarProps {
  modelId: string
  search: string
  onSearchChange: (value: string) => void
  sort: string
  onSortChange: (value: string) => void
  storeGroupId: string
  onStoreGroupIdChange: (value: string) => void
  onRefresh: () => void
  isRefreshing?: boolean
  onResetOverrides: () => void
  isResetting: boolean
}

export function MembersFilterBar({
  modelId,
  search,
  onSearchChange,
  sort,
  onSortChange,
  storeGroupId,
  onStoreGroupIdChange,
  onRefresh,
  isRefreshing,
  onResetOverrides,
  isResetting,
}: MembersFilterBarProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { data: groupsData } = useModelGroups(modelId, '')
  const groups = groupsData?.data ?? []

  return (
    <>
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
        {/* Search Input */}
        <div className="relative flex-1 max-w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search stores..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-9 bg-muted/50"
          />
        </div>

        {/* Sort */}
        <Select value={sort} onValueChange={onSortChange}>
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Group filter */}
        {groups.length > 0 && (
          <Select
            value={storeGroupId || 'all'}
            onValueChange={v => onStoreGroupIdChange(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map(g => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Refresh */}
        <Button
          variant="outline"
          size="icon"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="cursor-pointer"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </Button>

        {/* Reset Overrides */}
        <Button
          variant="destructive"
          onClick={() => setConfirmOpen(true)}
          disabled={isResetting}
          className="cursor-pointer"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset Overrides
        </Button>
      </div>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Reset All Overrides"
        description="This will remove all manual allocation overrides and revert to calculated values. This action cannot be undone."
        confirmText="Reset"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmOpen(false)
          onResetOverrides()
        }}
        variant="destructive"
        isLoading={isResetting}
      />
    </>
  )
}
